// LeadTreatmentSerializer accepts the treatment name slug. Keep the same detail
// (including assignment/notification metadata) that treatment-only creation uses.
export function buildManualLeadPayload(leadData, treatmentDetail) {
    const payload = {
        ...leadData,
        suppress_initial_message_sequence: true,
        // Only an explicit practice-email opt-in overrides the manual default.
        suppress_practice_notification: leadData?.suppress_practice_notification !== false,
    };
    if (!treatmentDetail?.treatment) return payload;
    return { ...payload, lead_treatments: [{ ...treatmentDetail }] };
}

// The API does NOT echo back the string we submit. LeadTreatmentSerializer
// resolves `treatment` through PracticeTreatment.get_by_name_or_alias, which
// matches case-insensitively AND accepts treatment-item aliases, but it
// serializes the row back as the canonical `treatment_item__name`. So a lead
// created from "invisalign" (or an alias) comes back as "Invisalign" and a
// strict === comparison reported a successful create as a failure.
// Case and surrounding whitespace are therefore normalised before comparing.
// A genuinely different name still fails loudly: this must never bind a
// reminder to a treatment the operator did not pick, so there is deliberately
// no "just take the only row" fallback here.
const sameTreatmentName = (a, b) =>
    String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

export function getCreatedManualLeadTreatment(leadData, treatmentDetail) {
    if (!treatmentDetail?.treatment) return undefined;
    const rows = Array.isArray(leadData?.lead_treatments) ? leadData.lead_treatments : [];
    const treatment =
        rows.find((row) => row?.treatment === treatmentDetail.treatment && row?.id)
        || rows.find((row) => sameTreatmentName(row?.treatment, treatmentDetail.treatment) && row?.id);
    if (!treatment) {
        const error = new Error(`Lead was created${leadData?.id ? ` (ID ${leadData.id})` : ''}, but its selected treatment was not returned. Please review the created lead before trying again.`);
        error.leadId = leadData?.id;
        // Carried into Sentry so a recurrence shows whether this is an alias
        // mismatch (submitted an alias, API returned the canonical name)
        // rather than a genuinely absent row.
        error.expectedTreatment = treatmentDetail.treatment;
        error.returnedTreatments = rows.map((row) => row?.treatment).filter(Boolean);
        throw error;
    }
    return treatment;
}

// The create-lead wizard keeps UI-only keys next to the API fields in
// personalDetail (phoneNumber, firstName, existingLeadSelection, ...). The Dentally
// booking path builds its lead POST from this whitelist so it carries exactly the
// same fields as the standard path: source, sex, registered flag, address,
// preferred contact, NHS and emergency contact.
const MANUAL_LEAD_FIELDS = [
    'first_name', 'last_name', 'salutation', 'sex', 'dob', 'is_registered_patient',
    'lead_contact', 'lead_address', 'lead_source', 'nhs_number',
    'emergency_contact_name', 'emergency_contact_phone',
];

export function pickManualLeadFields(personalDetail) {
    const out = {};
    if (!personalDetail) return out;
    MANUAL_LEAD_FIELDS.forEach((key) => {
        const value = personalDetail[key];
        if (value === undefined || value === null || value === '') return;
        out[key] = value && typeof value === 'object' && !Array.isArray(value) ? { ...value } : value;
    });
    return out;
}
